/**
 * TenderBuilder.tsx
 * 
 * Main tender response builder component with three-pane layout:
 * - Left: Section tree from template schema
 * - Center: Section editor with AI drafting
 * - Right: Gaps & Evidence panel
 * 
 * Integrates with TenderPipelineService for document analysis and section drafting.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Spinner, SpinnerSize, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Dropdown, IDropdownOption, TextField } from '@fluentui/react';
import { marked } from 'marked';
import {
    DocumentRegular,
    DismissRegular,
    ArrowDownloadRegular,
    CheckmarkCircleFilled,
    WarningFilled,
    ErrorCircleFilled,
    SparkleRegular,
    DocumentTextRegular,
    ClipboardTaskListLtr24Regular,
    ShieldCheckmark24Regular,
    Info24Regular,
    ShareRegular,
    AddRegular,
    ArrowSyncRegular,
    DeleteRegular,
    PeopleRegular
} from '@fluentui/react-icons';
import {
    TenderWorkspace,
    TemplateSection,
    Requirement,
    GapItem,
    PipelineStatus,
    runTenderPipelineWithPolling,
    getPipelineStatus,
    cancelTenderPipeline,
    draftTenderSection,
    getTenderGaps,
    resolveTenderGap,
    getTenderRequirements,
    exportWorkspace,
    getWorkspace,
    addTenderDocument,
    DocumentRole,
    deleteWorkspace,
    updateWorkspaceSharing
} from '../../api/documentWorkspaceApi';
import { DocumentWorkspaceSharingModal } from '../DocumentDraft/DocumentWorkspaceSharingModal';
import { authenticatedFetch } from '../../utils/authApi';
import styles from './TenderBuilder.module.css';

// Configure marked for safe HTML output
marked.setOptions({
    breaks: true,  // Convert \n to <br>
    gfm: true      // GitHub Flavored Markdown
});

// Helper to convert markdown to HTML for display
const markdownToHtml = (markdown: string): string => {
    if (!markdown) return '';
    // Check if content is already HTML (starts with < tag)
    if (markdown.trim().startsWith('<')) {
        return markdown;
    }
    // Convert markdown to HTML
    const html = marked.parse(markdown);
    return typeof html === 'string' ? html : '';
};

// Helper to detect if content contains Hebrew (RTL)
const isRTLContent = (content: string): boolean => {
    if (!content) return false;
    // Count Hebrew characters vs Latin characters
    const hebrewCount = (content.match(/[\u0590-\u05FF]/g) || []).length;
    const latinCount = (content.match(/[a-zA-Z]/g) || []).length;
    // If more than 20% Hebrew or Hebrew dominates, it's RTL
    return hebrewCount > 10 && (hebrewCount > latinCount || hebrewCount / (hebrewCount + latinCount) > 0.2);
};

// ============================================================================
// TYPES
// ============================================================================

interface TenderBuilderProps {
    workspace: TenderWorkspace;
    onClose: () => void;
    onWorkspaceUpdated?: (workspace: TenderWorkspace) => void;
}

type RightPanelTab = 'gaps' | 'requirements' | 'evidence';

interface PipelineStep {
    id: string;
    label: string;
    status: 'pending' | 'running' | 'completed' | 'error';
    message?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const TenderBuilder: React.FC<TenderBuilderProps> = ({
    workspace: initialWorkspace,
    onClose,
    onWorkspaceUpdated
}) => {
    // State
    const [workspace, setWorkspace] = useState<TenderWorkspace>(initialWorkspace);
    const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
    const [rightPanelTab, setRightPanelTab] = useState<RightPanelTab>('gaps');
    const [isRunningPipeline, setIsRunningPipeline] = useState(false);
    const pipelineRanRef = useRef(false);  // Track if pipeline already executed (ref to avoid re-renders)
    const [isDraftingSection, setIsDraftingSection] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');
    const [pipelineSteps, setPipelineSteps] = useState<PipelineStep[]>([]);
    const [gaps, setGaps] = useState<GapItem[]>(workspace.gaps || []);
    const [requirements, setRequirements] = useState<Requirement[]>(workspace.requirements || []);
    const [error, setError] = useState<string | null>(null);
    const [gapAnswers, setGapAnswers] = useState<Record<string, string>>({});
    
    // Modal states
    const [showSharingModal, setShowSharingModal] = useState(false);
    const [showAddDocModal, setShowAddDocModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [addDocForm, setAddDocForm] = useState<{ file: File | null; role: DocumentRole }>({ file: null, role: 'evidence' });
    const [isAddingDoc, setIsAddingDoc] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    
    // AI instruction input states (like Document Workspace)
    const [sectionInstruction, setSectionInstruction] = useState('');
    const [showInstructionInput, setShowInstructionInput] = useState(false);
    const [newSectionTitle, setNewSectionTitle] = useState('');
    const [showAddSection, setShowAddSection] = useState(false);

    // Get selected section
    const selectedSection = workspace.templateSchema?.find(s => s.id === selectedSectionId) || null;

    // Get workspace content for selected section
    const sectionContent = workspace.sections?.find(s => s.id === selectedSectionId)?.content || '';

    // Calculate coverage stats
    const totalRequirements = requirements.length;
    const coveredRequirements = requirements.filter(r => r.coverage_status === 'covered').length;
    const partialRequirements = requirements.filter(r => r.coverage_status === 'partial').length;
    const coveragePercent = totalRequirements > 0 
        ? Math.round((coveredRequirements + partialRequirements * 0.5) / totalRequirements * 100) 
        : 0;
    const openBlockers = gaps.filter(g => g.severity === 'blocker' && g.status === 'open').length;

    // ========================================================================
    // PIPELINE EXECUTION
    // ========================================================================

    const runPipeline = useCallback(async (force: boolean = false) => {
        // Prevent double execution (unless force=true)
        if (pipelineRanRef.current && !force) {
            console.log('[TenderBuilder] Pipeline already running or completed, skipping');
            return;
        }
        
        pipelineRanRef.current = true;  // Mark as executed immediately
        setIsRunningPipeline(true);
        setError(null);
        
        // Initialize pipeline steps with generic labels
        const initialSteps: PipelineStep[] = [
            { id: 'index', label: 'Processing documents', status: 'running' },
            { id: 'parse-template', label: 'Analyzing structure', status: 'pending' },
            { id: 'extract-requirements', label: 'Identifying sections', status: 'pending' },
            { id: 'map-requirements', label: 'Mapping content', status: 'pending' },
            { id: 'link-evidence', label: 'Gathering data', status: 'pending' },
            { id: 'draft-sections', label: 'Generating content', status: 'pending' },
            { id: 'analyze-gaps', label: 'Reviewing completeness', status: 'pending' }
        ];
        setPipelineSteps(initialSteps);

        try {
            // Use polling-based pipeline (avoids SSE timeout issues)
            await runTenderPipelineWithPolling(
                workspace.id,
                (status: PipelineStatus) => {
                    // Map progress percentage to step status
                    const progressStepMap: { threshold: number; stepId: string }[] = [
                        { threshold: 10, stepId: 'index' },
                        { threshold: 25, stepId: 'parse-template' },
                        { threshold: 40, stepId: 'extract-requirements' },
                        { threshold: 55, stepId: 'map-requirements' },
                        { threshold: 70, stepId: 'link-evidence' },
                        { threshold: 85, stepId: 'draft-sections' },
                        { threshold: 95, stepId: 'analyze-gaps' },
                    ];
                    
                    setPipelineSteps(prev => prev.map(step => {
                        const stepConfig = progressStepMap.find(s => s.stepId === step.id);
                        if (!stepConfig) return step;
                        
                        if (status.pipelineProgress >= stepConfig.threshold) {
                            return { ...step, status: 'completed', message: 'Done' };
                        } else if (status.pipelineProgress >= stepConfig.threshold - 10) {
                            return { ...step, status: 'running', message: status.pipelineCurrentStep };
                        }
                        return step;
                    }));
                },
                force
            );
            
            // Mark all steps complete
            setPipelineSteps(prev => prev.map(step => ({
                ...step,
                status: 'completed',
                message: 'Done'
            })));

            // Refresh workspace data after pipeline
            const updatedWorkspace = await getWorkspace(workspace.id) as TenderWorkspace;
            if (updatedWorkspace) {
                setWorkspace(updatedWorkspace);
                setGaps(updatedWorkspace.gaps || []);
                setRequirements(updatedWorkspace.requirements || []);
                onWorkspaceUpdated?.(updatedWorkspace);
            }

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Pipeline failed');
            // Mark current step as error
            setPipelineSteps(prev => {
                const runningStep = prev.find(s => s.status === 'running');
                if (runningStep) {
                    return prev.map(s => 
                        s.id === runningStep.id ? { ...s, status: 'error' as const } : s
                    );
                }
                return prev;
            });
        } finally {
            setIsRunningPipeline(false);
        }
    }, [workspace.id, onWorkspaceUpdated]);

    // Auto-run pipeline if workspace is newly created AND has documents
    // Also detect if backend already started the pipeline (pipelineRunId is set)
    useEffect(() => {
        const hasDocuments = workspace.documents && workspace.documents.length > 0;
        
        // Check if pipeline is stale (started more than 30 minutes ago)
        let isStale = false;
        if (workspace.pipelineStartedAt) {
            try {
                const startedAt = new Date(workspace.pipelineStartedAt);
                const ageMinutes = (Date.now() - startedAt.getTime()) / 60000;
                if (ageMinutes > 30 && (workspace.pipelineProgress ?? 0) < 100) {
                    isStale = true;
                    console.log(`[TenderBuilder] Pipeline is stale (${ageMinutes.toFixed(0)} min old)`);
                }
            } catch (e) {
                console.warn('[TenderBuilder] Could not parse pipelineStartedAt:', e);
            }
        }
        
        const pipelineAlreadyRunning = workspace.pipelineRunId && 
            (workspace.pipelineProgress ?? 0) < 100 && 
            !workspace.pipelineError &&
            !isStale;  // Don't treat stale pipelines as "running"
        
        // If pipeline is stale, show error immediately
        if (isStale && !pipelineRanRef.current) {
            pipelineRanRef.current = true;
            setError('Pipeline appears stuck (timed out). Click below to reset and try again.');
            // Don't show the processing overlay
            return;
        }
        
        // If backend already started the pipeline, just poll for status (use generic step labels)
        if (pipelineAlreadyRunning && !pipelineRanRef.current) {
            console.log('[TenderBuilder] Pipeline already running from backend, starting to poll for status');
            pipelineRanRef.current = true;
            setIsRunningPipeline(true);
            
            // Initialize pipeline steps based on current progress (generic labels)
            const initialSteps: PipelineStep[] = [
                { id: 'index', label: 'Processing documents', status: (workspace.pipelineProgress ?? 0) >= 10 ? 'completed' : 'running' },
                { id: 'parse-template', label: 'Analyzing structure', status: (workspace.pipelineProgress ?? 0) >= 25 ? 'completed' : ((workspace.pipelineProgress ?? 0) >= 10 ? 'running' : 'pending') },
                { id: 'extract-requirements', label: 'Identifying sections', status: (workspace.pipelineProgress ?? 0) >= 40 ? 'completed' : ((workspace.pipelineProgress ?? 0) >= 25 ? 'running' : 'pending') },
                { id: 'map-requirements', label: 'Mapping content', status: (workspace.pipelineProgress ?? 0) >= 55 ? 'completed' : ((workspace.pipelineProgress ?? 0) >= 40 ? 'running' : 'pending') },
                { id: 'link-evidence', label: 'Gathering data', status: (workspace.pipelineProgress ?? 0) >= 70 ? 'completed' : ((workspace.pipelineProgress ?? 0) >= 55 ? 'running' : 'pending') },
                { id: 'draft-sections', label: 'Generating content', status: (workspace.pipelineProgress ?? 0) >= 85 ? 'completed' : ((workspace.pipelineProgress ?? 0) >= 70 ? 'running' : 'pending') },
                { id: 'analyze-gaps', label: 'Reviewing completeness', status: (workspace.pipelineProgress ?? 0) >= 95 ? 'completed' : ((workspace.pipelineProgress ?? 0) >= 85 ? 'running' : 'pending') }
            ];
            setPipelineSteps(initialSteps);
        }
        
        // If workspace is newly created with documents and no pipeline running, start one
        if ((workspace.tenderState === 'created' || workspace.tenderState === 'ingested') && !pipelineRanRef.current && hasDocuments && !pipelineAlreadyRunning) {
            runPipeline();
        }
    }, [workspace.tenderState, workspace.documents, workspace.pipelineRunId, workspace.pipelineProgress, workspace.pipelineError, workspace.pipelineStartedAt, runPipeline]);
    
    // Separate effect for polling when pipeline is running
    useEffect(() => {
        if (!isRunningPipeline) return;
        
        console.log('[TenderBuilder] Starting pipeline status polling...');
        
        const pollInterval = setInterval(async () => {
            try {
                const status = await getPipelineStatus(workspace.id);
                console.log('[TenderBuilder] Poll status:', status.pipelineProgress, status.pipelineCurrentStep, status.isComplete, status.isStale);
                
                // Check for stale pipeline first
                if (status.isStale) {
                    clearInterval(pollInterval);
                    setError(status.pipelineError || 'Pipeline appears stuck. Click "Cancel Analysis" to reset and try again.');
                    // Keep showing the processing UI so user can click Cancel
                    return;
                }
                
                // Update steps based on progress
                const progressStepMap: { threshold: number; stepId: string }[] = [
                    { threshold: 10, stepId: 'index' },
                    { threshold: 25, stepId: 'parse-template' },
                    { threshold: 40, stepId: 'extract-requirements' },
                    { threshold: 55, stepId: 'map-requirements' },
                    { threshold: 70, stepId: 'link-evidence' },
                    { threshold: 85, stepId: 'draft-sections' },
                    { threshold: 95, stepId: 'analyze-gaps' },
                ];
                
                setPipelineSteps(prev => prev.map(step => {
                    const stepConfig = progressStepMap.find(s => s.stepId === step.id);
                    if (!stepConfig) return step;
                    
                    if (status.pipelineProgress >= stepConfig.threshold) {
                        return { ...step, status: 'completed', message: 'Done' };
                    } else if (status.pipelineProgress >= stepConfig.threshold - 10) {
                        return { ...step, status: 'running', message: status.pipelineCurrentStep };
                    }
                    return step;
                }));
                
                // Update workspace with current step info for display
                setWorkspace(prev => ({
                    ...prev,
                    pipelineProgress: status.pipelineProgress,
                    pipelineCurrentStep: status.pipelineCurrentStep
                }));
                
                // Check if complete or error
                if (status.pipelineError) {
                    clearInterval(pollInterval);
                    setError(status.pipelineError);
                    setIsRunningPipeline(false);
                    return;
                }
                
                if (status.isComplete) {
                    clearInterval(pollInterval);
                    console.log('[TenderBuilder] Pipeline completed!');
                    
                    // Mark all steps complete
                    setPipelineSteps(prev => prev.map(step => ({
                        ...step,
                        status: 'completed',
                        message: 'Done'
                    })));
                    
                    // Refresh workspace data
                    const updatedWorkspace = await getWorkspace(workspace.id) as TenderWorkspace;
                    if (updatedWorkspace) {
                        setWorkspace(updatedWorkspace);
                        setGaps(updatedWorkspace.gaps || []);
                        setRequirements(updatedWorkspace.requirements || []);
                        onWorkspaceUpdated?.(updatedWorkspace);
                    }
                    setIsRunningPipeline(false);
                }
            } catch (err) {
                console.error('[TenderBuilder] Error polling pipeline status:', err);
            }
        }, 2000);  // Poll every 2 seconds
        
        return () => clearInterval(pollInterval);
    }, [isRunningPipeline, workspace.id, onWorkspaceUpdated]);

    // ========================================================================
    // SECTION DRAFTING
    // ========================================================================

    const draftSection = useCallback(async (instruction?: string) => {
        if (!selectedSectionId) return;
        
        setIsDraftingSection(true);
        setStreamingContent('');
        setError(null);
        setShowInstructionInput(false);

        try {
            let fullContent = '';
            await draftTenderSection(workspace.id, selectedSectionId, (chunk) => {
                fullContent += chunk;
                setStreamingContent(fullContent);
            }, instruction);
            
            // After drafting, update the workspace sections
            setWorkspace(prev => ({
                ...prev,
                sections: prev.sections?.map(s => 
                    s.id === selectedSectionId ? { ...s, content: fullContent, status: 'ai_generated' as const } : s
                ) || []
            }));
            
            // Clear instruction
            setSectionInstruction('');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to draft section');
        } finally {
            setIsDraftingSection(false);
        }
    }, [workspace.id, selectedSectionId]);

    // ========================================================================
    // GAP RESOLUTION
    // ========================================================================

    const handleResolveGap = useCallback(async (gapId: string, status: 'answered' | 'deferred') => {
        const answer = gapAnswers[gapId] || '';
        if (status === 'answered' && !answer.trim()) return;

        try {
            await resolveTenderGap(workspace.id, gapId, answer, status);
            setGaps(prev => prev.map(g => 
                g.id === gapId ? { ...g, status, user_answer: answer } : g
            ));
            setGapAnswers(prev => ({ ...prev, [gapId]: '' }));
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to resolve gap');
        }
    }, [workspace.id, gapAnswers]);

    // ========================================================================
    // EXPORT
    // ========================================================================
    
    // Track last export URL for quick re-download
    const [lastExportUrl, setLastExportUrl] = useState<string | null>(workspace.exportedUrl || null);

    const handleExport = useCallback(async () => {
        try {
            const downloadUrl = await exportWorkspace(workspace.id);
            setLastExportUrl(downloadUrl);  // Store for re-download
            window.open(downloadUrl, '_blank');
            // Refresh workspace to get updated status
            const updated = await getWorkspace(workspace.id);
            setWorkspace(updated as TenderWorkspace);
            onWorkspaceUpdated?.(updated as TenderWorkspace);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Export failed');
        }
    }, [workspace.id, onWorkspaceUpdated]);

    // Handle re-analyze (re-run pipeline)
    const handleReAnalyze = useCallback(async () => {
        pipelineRanRef.current = false;  // Reset so pipeline can run again
        await runPipeline(false);
    }, [runPipeline]);

    // Handle force resync (re-run pipeline ignoring cache)
    const handleForceResync = useCallback(async () => {
        pipelineRanRef.current = false;  // Reset so pipeline can run again
        await runPipeline(true);
    }, [runPipeline]);

    // Handle refresh workspace
    const refreshWorkspace = useCallback(async () => {
        try {
            const updated = await getWorkspace(workspace.id);
            setWorkspace(updated as TenderWorkspace);
            onWorkspaceUpdated?.(updated as TenderWorkspace);
        } catch (err) {
            setError('Failed to refresh workspace');
        }
    }, [workspace.id, onWorkspaceUpdated]);

    // Handle delete workspace
    const handleDelete = useCallback(async () => {
        try {
            await deleteWorkspace(workspace.id);
            onClose();
        } catch (err) {
            setError('Failed to delete workspace');
        }
    }, [workspace.id, onClose]);

    // Handle add new section
    const handleAddSection = useCallback(() => {
        if (!newSectionTitle.trim()) return;
        
        const newId = `custom-${Date.now()}`;
        const newSection: TemplateSection = {
            id: newId,
            title: newSectionTitle.trim(),
            level: 1,
            heading_path: newId,
            order: (workspace.templateSchema?.length || 0) + 1,
            expected_content_type: 'narrative',
            cues: [],
            required_fields: [],
            placeholder_slots: []
        };
        
        // Update workspace locally (will be synced on save)
        const now = new Date().toISOString();
        setWorkspace(prev => ({
            ...prev,
            templateSchema: [...(prev.templateSchema || []), newSection],
            sections: [...(prev.sections || []), {
                id: newId,
                title: newSectionTitle.trim(),
                level: 1,
                content: '',
                status: 'draft' as const,
                order: (prev.sections?.length || 0) + 1,
                mapped_requirement_ids: [],
                coverage_status: '',
                gap_count: 0,
                createdAt: now,
                updatedAt: now
            }]
        }));
        
        setNewSectionTitle('');
        setShowAddSection(false);
        setSelectedSectionId(newId);  // Select the new section
    }, [newSectionTitle, workspace.templateSchema?.length, workspace.sections?.length]);

    // Handle add document
    const handleAddDocument = useCallback(async () => {
        if (!addDocForm.file) return;
        
        setIsAddingDoc(true);
        setUploadProgress('Uploading file...');
        
        try {
            // Step 1: Upload using SAS (direct-to-blob) for better scalability
            const { sasUpload } = await import('../../utils/sasUpload');
            const result = await sasUpload(addDocForm.file, {
                ownerType: 'workspace',
                ownerId: workspace.id,
                onProgress: (progress) => {
                    if (progress < 70) {
                        setUploadProgress(`Uploading file... ${progress}%`);
                    }
                }
            });
            
            if (!result.success) {
                throw new Error(result.error || 'Upload failed');
            }
            
            const blobId = result.fileId!;
            
            // Step 2: Add to workspace
            setUploadProgress('Adding to workspace...');
            await addTenderDocument(workspace.id, blobId, addDocForm.file.name, addDocForm.role);
            
            setShowAddDocModal(false);
            setAddDocForm({ file: null, role: 'evidence' });
            setUploadProgress(null);
            await refreshWorkspace();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add document');
            setUploadProgress(null);
        } finally {
            setIsAddingDoc(false);
        }
    }, [workspace.id, addDocForm, refreshWorkspace]);

    // Handle sharing updated
    const handleSharingUpdated = useCallback(async () => {
        await refreshWorkspace();
        setShowSharingModal(false);
    }, [refreshWorkspace]);

    // ========================================================================
    // RENDER HELPERS
    // ========================================================================

    // Handle cancel/stop pipeline
    const handleCancelPipeline = useCallback(async () => {
        setIsRunningPipeline(false);
        pipelineRanRef.current = false;  // Allow restart
        
        try {
            // Call backend to persist the cancellation
            await cancelTenderPipeline(workspace.id);
            console.log('[TenderBuilder] Pipeline cancelled successfully');
            setError('Analysis cancelled. You can run it again when ready.');
        } catch (err) {
            console.error('[TenderBuilder] Error cancelling pipeline:', err);
            setError('Analysis cancelled locally (could not notify server)');
        }
    }, [workspace.id]);

    const renderPipelineProgress = () => (
        <div className={styles.pipelineOverlay}>
            <div className={styles.pipelineIcon}>
                <SparkleRegular style={{ fontSize: '36px', color: 'white' }} />
            </div>
            <div className={styles.pipelineTitle}>Analyzing Document Structure</div>
            <Spinner size={SpinnerSize.medium} style={{ marginBottom: '16px' }} />
            <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '24px' }}>
                {workspace.pipelineCurrentStep || 'Processing...'}
            </div>
            
            <div className={styles.pipelineSteps}>
                {pipelineSteps.map(step => (
                    <div 
                        key={step.id} 
                        className={`${styles.pipelineStep} ${styles[step.status]}`}
                    >
                        <div className={`${styles.pipelineStepIcon} ${styles[step.status]}`}>
                            {step.status === 'completed' ? (
                                <CheckmarkCircleFilled />
                            ) : step.status === 'running' ? (
                                <Spinner size={SpinnerSize.xSmall} />
                            ) : step.status === 'error' ? (
                                <ErrorCircleFilled />
                            ) : (
                                <span>•</span>
                            )}
                        </div>
                        <div className={styles.pipelineStepText}>
                            {step.label}
                            {step.message && step.status === 'running' && (
                                <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '12px' }}>
                                    - {step.message}
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Cancel button */}
            <button 
                onClick={handleCancelPipeline}
                style={{
                    marginTop: '24px',
                    padding: '8px 24px',
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    borderRadius: '6px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                }}
            >
                <DismissRegular /> Cancel Analysis
            </button>
        </div>
    );

    const renderSectionTree = () => (
        <div className={styles.sectionPanel}>
            <div className={styles.sectionPanelHeader}>
                <span className={styles.sectionPanelTitle}>Document Sections</span>
            </div>
            <div className={styles.sectionList}>
                {workspace.templateSchema?.map(section => {
                    // Find coverage status for this section
                    const sectionGaps = gaps.filter(g => g.section_id === section.id);
                    const hasBlocker = sectionGaps.some(g => g.severity === 'blocker' && g.status === 'open');
                    const hasWarning = sectionGaps.some(g => g.severity === 'warning' && g.status === 'open');

                    return (
                        <div
                            key={section.id}
                            className={`${styles.sectionItem} ${styles[`level${section.level}`]} ${selectedSectionId === section.id ? styles.selected : ''}`}
                            onClick={() => setSelectedSectionId(section.id)}
                        >
                            <span className={styles.sectionTitle}>{section.title}</span>
                            {hasBlocker ? (
                                <span className={`${styles.sectionBadge} ${styles.notCovered}`}>
                                    {sectionGaps.filter(g => g.severity === 'blocker').length} gaps
                                </span>
                            ) : hasWarning ? (
                                <span className={`${styles.sectionBadge} ${styles.partial}`}>
                                    {sectionGaps.filter(g => g.status === 'open').length} issues
                                </span>
                            ) : sectionGaps.length === 0 ? (
                                <span className={`${styles.sectionBadge} ${styles.covered}`}>✓</span>
                            ) : null}
                        </div>
                    );
                })}
                
                {(!workspace.templateSchema || workspace.templateSchema.length === 0) && (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af' }}>
                        <DocumentTextRegular style={{ fontSize: '32px', marginBottom: '8px', display: 'block' }} />
                        <span style={{ fontSize: '13px' }}>No sections yet. Run the pipeline to analyze your template.</span>
                    </div>
                )}
                
                {/* Add Section (like Document Workspace) */}
                {showAddSection ? (
                    <div style={{ 
                        padding: '12px', 
                        borderTop: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb'
                    }}>
                        <TextField
                            placeholder="New section title..."
                            value={newSectionTitle}
                            onChange={(_, v) => setNewSectionTitle(v || '')}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddSection()}
                            styles={{ root: { marginBottom: '8px' } }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <PrimaryButton
                                text="Add"
                                onClick={handleAddSection}
                                disabled={!newSectionTitle.trim()}
                                styles={{ root: { flex: 1 } }}
                            />
                            <DefaultButton
                                text="Cancel"
                                onClick={() => { setShowAddSection(false); setNewSectionTitle(''); }}
                                styles={{ root: { flex: 1 } }}
                            />
                        </div>
                    </div>
                ) : (
                    <button
                        className={styles.addSectionButton}
                        onClick={() => setShowAddSection(true)}
                        style={{
                            width: '100%',
                            padding: '12px',
                            border: 'none',
                            borderTop: '1px solid #e5e7eb',
                            backgroundColor: 'transparent',
                            color: '#6b7280',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            fontSize: '13px'
                        }}
                    >
                        <AddRegular />
                        Add Section
                    </button>
                )}
            </div>
        </div>
    );

    const renderEditor = () => (
        <div className={styles.editorPanel}>
            <div className={styles.editorHeader}>
                <span className={styles.editorTitle}>
                    {selectedSection?.title || 'Select a section'}
                </span>
                {selectedSection && (
                    <div className={styles.editorActions}>
                        {/* Toggle instruction input */}
                        <button 
                            className={`${styles.editorButton}`}
                            onClick={() => setShowInstructionInput(!showInstructionInput)}
                            title="Add custom instructions"
                        >
                            <Info24Regular />
                        </button>
                        <button 
                            className={`${styles.editorButton} ${styles.primary}`}
                            onClick={() => draftSection(sectionInstruction || undefined)}
                            disabled={isDraftingSection}
                        >
                            {isDraftingSection ? (
                                <Spinner size={SpinnerSize.xSmall} />
                            ) : (
                                <SparkleRegular />
                            )}
                            {isDraftingSection ? 'Drafting...' : sectionContent ? 'Enhance with AI' : 'Draft with AI'}
                        </button>
                    </div>
                )}
            </div>
            
            {/* AI Instruction Input (like Document Workspace) */}
            {showInstructionInput && selectedSection && (
                <div style={{ 
                    padding: '12px 16px', 
                    background: '#eff6ff', 
                    borderBottom: '1px solid #bfdbfe',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start'
                }}>
                    <TextField
                        placeholder="E.g. 'Focus on security aspects' or 'Add more technical details about integration'"
                        value={sectionInstruction}
                        onChange={(_, v) => setSectionInstruction(v || '')}
                        multiline
                        rows={2}
                        styles={{ 
                            root: { flex: 1 },
                            fieldGroup: { minHeight: '60px' }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.ctrlKey && sectionInstruction.trim()) {
                                draftSection(sectionInstruction);
                            }
                        }}
                    />
                    <PrimaryButton
                        text="Apply"
                        onClick={() => draftSection(sectionInstruction)}
                        disabled={!sectionInstruction.trim() || isDraftingSection}
                        styles={{ root: { minWidth: '80px' } }}
                    />
                </div>
            )}
            
            <div className={styles.editorContent}>
                {selectedSection ? (
                    <>
                        {/* Section metadata */}
                        <div style={{ 
                            padding: '12px 16px', 
                            background: '#f9fafb', 
                            borderRadius: '8px', 
                            marginBottom: '16px',
                            fontSize: '13px',
                            color: '#6b7280'
                        }}>
                            <div><strong>Type:</strong> {selectedSection.expected_content_type}</div>
                            {selectedSection.cues?.length > 0 && (
                                <div><strong>Cues:</strong> {selectedSection.cues.join(', ')}</div>
                            )}
                            {selectedSection.required_fields?.length > 0 && (
                                <div><strong>Required fields:</strong> {selectedSection.required_fields.join(', ')}</div>
                            )}
                        </div>

                        {/* Mapped requirements for this section */}
                        {requirements.filter(r => r.linked_section_ids?.includes(selectedSection.id)).length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                                    📋 Requirements to address:
                                </div>
                                {requirements
                                    .filter(r => r.linked_section_ids?.includes(selectedSection.id))
                                    .slice(0, 5)
                                    .map(req => (
                                        <div key={req.id} style={{
                                            padding: '8px 12px',
                                            background: '#fffbeb',
                                            border: '1px solid #fcd34d',
                                            borderRadius: '6px',
                                            marginBottom: '6px',
                                            fontSize: '12px'
                                        }}>
                                            <span style={{
                                                fontSize: '10px',
                                                padding: '2px 6px',
                                                background: req.mandatory_level === 'must' ? '#fee2e2' : '#dbeafe',
                                                borderRadius: '4px',
                                                marginRight: '8px'
                                            }}>
                                                {req.mandatory_level.toUpperCase()}
                                            </span>
                                            {req.text.slice(0, 150)}{req.text.length > 150 ? '...' : ''}
                                        </div>
                                    ))}
                            </div>
                        )}

                        {/* Content display/editor */}
                        {isDraftingSection ? (
                            <div 
                                className={styles.streamingContent}
                                dir={isRTLContent(streamingContent) ? 'rtl' : 'ltr'}
                                style={{ textAlign: isRTLContent(streamingContent) ? 'right' : 'left' }}
                                dangerouslySetInnerHTML={{ __html: markdownToHtml(streamingContent) || 'Generating content...' }}
                            />
                        ) : sectionContent ? (
                            <div 
                                className={styles.sectionEditor}
                                dir={isRTLContent(sectionContent) ? 'rtl' : 'ltr'}
                                style={{ textAlign: isRTLContent(sectionContent) ? 'right' : 'left' }}
                                dangerouslySetInnerHTML={{ __html: markdownToHtml(sectionContent) }}
                            />
                        ) : (
                            <div className={styles.editorPlaceholder}>
                                <SparkleRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                                <div>No content yet</div>
                                <div style={{ fontSize: '13px', marginTop: '8px' }}>
                                    Click "Draft with AI" to generate content for this section
                                </div>
                            </div>
                        )}
                    </>
                ) : (!workspace.documents || workspace.documents.length === 0) ? (
                    <div className={styles.editorPlaceholder}>
                        <AddRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5, color: '#0ea5e9' }} />
                        <div style={{ fontSize: '18px', fontWeight: 500 }}>Upload a Document to Begin</div>
                        <div style={{ fontSize: '13px', marginTop: '8px', maxWidth: '300px', textAlign: 'center' }}>
                            Click "Add Doc" above to upload a document. The AI will analyze its structure and help you fill in the sections.
                        </div>
                        <button 
                            className={styles.headerButton} 
                            onClick={() => setShowAddDocModal(true)}
                            style={{ marginTop: '16px', padding: '8px 16px' }}
                        >
                            <AddRegular /> Add Document
                        </button>
                    </div>
                ) : (
                    <div className={styles.editorPlaceholder}>
                        <DocumentRegular style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                        <div>Select a section to start editing</div>
                        <div style={{ fontSize: '13px', marginTop: '8px' }}>
                            Choose a section from the left panel to view and generate content
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderRightPanel = () => (
        <div className={styles.rightPanel}>
            {/* Stats summary */}
            <div className={styles.statsSummary}>
                <div className={`${styles.statItem} ${coveragePercent >= 80 ? styles.success : coveragePercent >= 50 ? styles.warning : styles.error}`}>
                    <div className={styles.statValue}>{coveragePercent}%</div>
                    <div className={styles.statLabel}>Coverage</div>
                </div>
                <div className={`${styles.statItem} ${openBlockers > 0 ? styles.error : styles.success}`}>
                    <div className={styles.statValue}>{openBlockers}</div>
                    <div className={styles.statLabel}>Blockers</div>
                </div>
                <div className={styles.statItem}>
                    <div className={styles.statValue}>{totalRequirements}</div>
                    <div className={styles.statLabel}>Requirements</div>
                </div>
            </div>

            {/* Tabs */}
            <div className={styles.rightPanelTabs}>
                <button
                    className={`${styles.rightPanelTab} ${rightPanelTab === 'gaps' ? styles.active : ''}`}
                    onClick={() => setRightPanelTab('gaps')}
                >
                    <WarningFilled style={{ marginRight: '4px' }} />
                    Gaps ({gaps.filter(g => g.status === 'open').length})
                </button>
                <button
                    className={`${styles.rightPanelTab} ${rightPanelTab === 'requirements' ? styles.active : ''}`}
                    onClick={() => setRightPanelTab('requirements')}
                >
                    <ClipboardTaskListLtr24Regular style={{ marginRight: '4px' }} />
                    Reqs
                </button>
                <button
                    className={`${styles.rightPanelTab} ${rightPanelTab === 'evidence' ? styles.active : ''}`}
                    onClick={() => setRightPanelTab('evidence')}
                >
                    <ShieldCheckmark24Regular style={{ marginRight: '4px' }} />
                    Evidence
                </button>
            </div>

            <div className={styles.rightPanelContent}>
                {/* Gaps tab */}
                {rightPanelTab === 'gaps' && (
                    <>
                        {gaps.filter(g => g.status === 'open').length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                                <CheckmarkCircleFilled style={{ fontSize: '32px', color: '#22c55e', marginBottom: '8px' }} />
                                <div>No open gaps!</div>
                            </div>
                        ) : (
                            gaps.filter(g => g.status === 'open').map(gap => (
                                <div 
                                    key={gap.id} 
                                    className={`${styles.gapItem} ${gap.severity === 'warning' ? styles.warning : gap.severity === 'info' ? styles.info : ''}`}
                                >
                                    <div className={styles.gapHeader}>
                                        <span className={styles.gapIcon}>
                                            {gap.severity === 'blocker' ? '🚫' : gap.severity === 'warning' ? '⚠️' : 'ℹ️'}
                                        </span>
                                        <span className={styles.gapTitle}>{gap.gap_type.replace('_', ' ')}</span>
                                    </div>
                                    <div className={styles.gapDescription}>{gap.description}</div>
                                    {gap.question_to_user && (
                                        <div className={styles.gapQuestion}>"{gap.question_to_user}"</div>
                                    )}
                                    <textarea
                                        className={styles.gapInput}
                                        placeholder="Provide information to resolve this gap..."
                                        value={gapAnswers[gap.id] || ''}
                                        onChange={(e) => setGapAnswers(prev => ({ ...prev, [gap.id]: e.target.value }))}
                                    />
                                    <div className={styles.gapActions}>
                                        <button
                                            className={`${styles.gapButton} ${styles.resolve}`}
                                            onClick={() => handleResolveGap(gap.id, 'answered')}
                                            disabled={!gapAnswers[gap.id]?.trim()}
                                        >
                                            Resolve
                                        </button>
                                        <button
                                            className={`${styles.gapButton} ${styles.defer}`}
                                            onClick={() => handleResolveGap(gap.id, 'deferred')}
                                        >
                                            Defer
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}

                {/* Requirements tab */}
                {rightPanelTab === 'requirements' && (
                    <>
                        {requirements.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                                <ClipboardTaskListLtr24Regular style={{ fontSize: '32px', marginBottom: '8px' }} />
                                <div>No requirements extracted yet</div>
                            </div>
                        ) : (
                            requirements.map(req => (
                                <div key={req.id} className={styles.requirementItem}>
                                    <div className={styles.requirementHeader}>
                                        <span className={`${styles.requirementType} ${styles[req.mandatory_level]}`}>
                                            {req.mandatory_level}
                                        </span>
                                        <span style={{ fontSize: '10px', color: '#6b7280' }}>
                                            {req.requirement_type}
                                        </span>
                                    </div>
                                    <div className={styles.requirementText}>
                                        {req.text.slice(0, 200)}{req.text.length > 200 ? '...' : ''}
                                    </div>
                                    <div className={styles.requirementCoverage}>
                                        Status: 
                                        <span style={{
                                            marginLeft: '4px',
                                            fontWeight: 500,
                                            color: req.coverage_status === 'covered' ? '#16a34a' : 
                                                   req.coverage_status === 'partial' ? '#d97706' : '#dc2626'
                                        }}>
                                            {req.coverage_status}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </>
                )}

                {/* Evidence tab */}
                {rightPanelTab === 'evidence' && (
                    <div style={{ textAlign: 'center', padding: '24px', color: '#9ca3af' }}>
                        <ShieldCheckmark24Regular style={{ fontSize: '32px', marginBottom: '8px' }} />
                        <div>Evidence linking in progress</div>
                        <div style={{ fontSize: '12px', marginTop: '8px' }}>
                            Evidence from uploaded documents will be linked to requirements automatically.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    // ========================================================================
    // MAIN RENDER
    // ========================================================================

    return (
        <div className={styles.container} style={{ position: 'relative' }}>
            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIcon}>
                        <DocumentRegular style={{ fontSize: '24px' }} />
                    </div>
                    <div>
                        <h1 className={styles.headerTitle}>{workspace.title}</h1>
                        <p className={styles.headerSubtitle}>
                            {workspace.documents?.length || 0} documents • {workspace.templateSchema?.length || 0} sections
                        </p>
                    </div>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.headerButton} onClick={() => setShowAddDocModal(true)} title="Add Document">
                        <AddRegular />
                        Add Doc
                    </button>
                    <button className={styles.headerButton} onClick={handleReAnalyze} disabled={isRunningPipeline} title="Re-analyze with updated documents">
                        <ArrowSyncRegular />
                        Re-analyze
                    </button>
                    <button className={styles.headerButton} onClick={handleForceResync} disabled={isRunningPipeline} title="Force full re-analysis (ignores cache)">
                        <ArrowSyncRegular />
                    </button>
                    <button className={styles.headerButton} onClick={() => setShowSharingModal(true)} title="Share workspace">
                        <ShareRegular />
                        Share
                    </button>
                    <button className={styles.headerButton} onClick={handleExport}>
                        <ArrowDownloadRegular />
                        Export
                    </button>
                    {lastExportUrl && (
                        <button 
                            className={styles.headerButton} 
                            onClick={() => window.open(lastExportUrl, '_blank')}
                            title="Re-download last export"
                            style={{ fontSize: '12px', padding: '4px 8px' }}
                        >
                            <ArrowDownloadRegular />
                            Last
                        </button>
                    )}
                    <button className={styles.headerButton} onClick={() => setShowDeleteConfirm(true)} title="Delete workspace" style={{ color: '#dc2626' }}>
                        <DeleteRegular />
                    </button>
                    <button className={styles.headerButton} onClick={onClose}>
                        <DismissRegular />
                    </button>
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <div style={{
                    padding: '12px 24px',
                    background: '#fef2f2',
                    borderBottom: '1px solid #fecaca',
                    color: '#dc2626',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <ErrorCircleFilled />
                    {error}
                    {/* Show Reset button for stuck/stale pipeline errors */}
                    {(error.includes('stuck') || error.includes('timed out')) && (
                        <button
                            onClick={async () => {
                                try {
                                    await cancelTenderPipeline(workspace.id);
                                    setError(null);
                                    pipelineRanRef.current = false;  // Allow restart
                                    // Refresh workspace
                                    const refreshed = await getWorkspace(workspace.id) as TenderWorkspace;
                                    if (refreshed) {
                                        setWorkspace(refreshed);
                                        onWorkspaceUpdated?.(refreshed);
                                    }
                                } catch (e) {
                                    console.error('Failed to reset pipeline:', e);
                                }
                            }}
                            style={{ 
                                marginLeft: '8px',
                                padding: '4px 12px',
                                background: '#dc2626',
                                border: 'none',
                                borderRadius: '4px',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 500
                            }}
                        >
                            Reset Pipeline
                        </button>
                    )}
                    <button 
                        onClick={() => setError(null)}
                        style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                    >
                        <DismissRegular />
                    </button>
                </div>
            )}

            {/* Pipeline progress overlay */}
            {isRunningPipeline && renderPipelineProgress()}

            {/* Main three-pane layout */}
            <div className={styles.mainContent}>
                {renderSectionTree()}
                {renderEditor()}
                {renderRightPanel()}
            </div>

            {/* Sharing Modal */}
            <DocumentWorkspaceSharingModal
                workspace={workspace}
                isOpen={showSharingModal}
                onClose={() => setShowSharingModal(false)}
                onUpdated={(updated) => {
                    setWorkspace(updated as TenderWorkspace);
                    onWorkspaceUpdated?.(updated as TenderWorkspace);
                    setShowSharingModal(false);
                }}
            />

            {/* Add Document Modal */}
            <Dialog
                hidden={!showAddDocModal}
                onDismiss={() => { setShowAddDocModal(false); setAddDocForm({ file: null, role: 'evidence' }); }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Add Document',
                    subText: 'Upload a document to add to this tender workspace'
                }}
                modalProps={{ isBlocking: false }}
            >
                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.xlsm,.pptx,.ppt,.txt"
                    onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                            setAddDocForm(prev => ({ ...prev, file: e.target.files![0] }));
                        }
                        e.target.value = '';
                    }}
                />
                
                {/* File drop zone */}
                <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                        border: '2px dashed #d1d5db',
                        borderRadius: '8px',
                        padding: '24px',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: addDocForm.file ? '#f0fdf4' : '#f9fafb',
                        marginBottom: '16px'
                    }}
                >
                    {addDocForm.file ? (
                        <div>
                            <CheckmarkCircleFilled style={{ fontSize: '24px', color: '#22c55e', marginBottom: '8px' }} />
                            <div style={{ fontWeight: 600 }}>{addDocForm.file.name}</div>
                            <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {(addDocForm.file.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                        </div>
                    ) : (
                        <div>
                            <AddRegular style={{ fontSize: '24px', color: '#9ca3af', marginBottom: '8px' }} />
                            <div style={{ color: '#374151' }}>Click to select a file</div>
                            <div style={{ fontSize: '12px', color: '#9ca3af' }}>PDF, Word, Excel, PowerPoint</div>
                        </div>
                    )}
                </div>
                
                <Dropdown
                    label="Document Role"
                    selectedKey={addDocForm.role}
                    options={[
                        { key: 'template', text: 'Template (response structure)' },
                        { key: 'requirements', text: 'Requirements (RFP/specs)' },
                        { key: 'evidence', text: 'Evidence (past projects, certs)' },
                        { key: 'standards', text: 'Standards (ISO, compliance)' },
                        { key: 'commercial', text: 'Commercial (pricing, terms)' }
                    ]}
                    onChange={(_, opt) => opt && setAddDocForm(prev => ({ ...prev, role: opt.key as DocumentRole }))}
                />
                
                {uploadProgress && (
                    <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280' }}>
                        <Spinner size={SpinnerSize.xSmall} />
                        {uploadProgress}
                    </div>
                )}
                
                <DialogFooter>
                    <PrimaryButton onClick={handleAddDocument} text="Upload & Add" disabled={isAddingDoc || !addDocForm.file} />
                    <DefaultButton onClick={() => { setShowAddDocModal(false); setAddDocForm({ file: null, role: 'evidence' }); }} text="Cancel" />
                </DialogFooter>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                hidden={!showDeleteConfirm}
                onDismiss={() => setShowDeleteConfirm(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Delete Workspace?',
                    subText: 'This will permanently delete this tender workspace and all its documents. This action cannot be undone.'
                }}
                modalProps={{ isBlocking: true }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={handleDelete} text="Delete" style={{ backgroundColor: '#dc2626', borderColor: '#dc2626' }} />
                    <DefaultButton onClick={() => setShowDeleteConfirm(false)} text="Cancel" />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default TenderBuilder;
